import type { RefObject } from 'react';
import type { GridApi } from '@mui/x-data-grid-premium';
import { authService } from '@/services/auth/authService';

/**
 * Shared helpers for capturing and applying MUI DataGrid Premium layout state.
 *
 * Used by both the transient per-device auto-persistence ({@link useGridStatePersistence})
 * and the DB-backed named "saved views" ({@link GridViewSelector}) so both produce
 * and consume an identical layout payload.
 *
 * A "layout" is the subset of `apiRef.exportState()` that describes presentation:
 * column visibility/order/width, sort model, filter model, pagination, density.
 */

export const GRID_STATE_STORAGE_PREFIX = 'nexo-grid-state:';

/**
 * MUI's row-grouping column: `__row_group_by_columns_group__` with the default
 * `rowGroupingColumnMode: 'single'`, `__row_group_by_columns_group_<field>__` with 'multiple'.
 */
const ROW_GROUPING_SINGLE_FIELD = '__row_group_by_columns_group__';
const isRowGroupingField = (field: string) => field.startsWith('__row_group_by_columns_group');

/**
 * Put the row-grouping column back where the grid puts it — first, after `__check__`.
 *
 * `exportState()` runs on the grouping-model change event, one beat BEFORE the grid re-hydrates
 * its columns with the grouping column, so the captured `orderedFields` describes the ungrouped
 * grid while `rowGrouping.model` already describes the grouped one. Restoring that as-is sends the
 * grouping column to the far right, because MUI appends columns missing from `orderedFields` after
 * the imported ones. The column is `disableReorder: true`, so there is no user-chosen position to
 * preserve — first is the only correct answer.
 */
function orderWithGroupingColumn(orderedFields: string[], rowGroupingModel: string[] | undefined): string[] {
  const dataFields = orderedFields.filter(
    (f) => f !== '__check__' && f !== '_spacer' && !isRowGroupingField(f),
  );
  const groupingFields = orderedFields.filter(isRowGroupingField);
  if (groupingFields.length === 0 && (rowGroupingModel?.length ?? 0) > 0) {
    groupingFields.push(ROW_GROUPING_SINGLE_FIELD);
  }
  return [...groupingFields, ...dataFields];
}

/**
 * localStorage key for a grid's transient last-used layout.
 *
 * Scoped by tenant + effective user: layouts are personal preferences but localStorage is
 * per-BROWSER, so an unscoped key hands user A's filter/visibility model to whoever signs
 * in next on the same machine — and a leftover filter silently hides their rows. The
 * effective user (the impersonated identity during "Run as user") is deliberate: viewing
 * as someone means their layout slate, not the admin's. Pre-existing unscoped entries are
 * simply orphaned — these are transient per-device conveniences, not data.
 */
export function gridStateStorageKey(gridKey: string): string {
  const tenantId = authService.getTenantId() ?? '-';
  const userId = authService.getCurrentUser()?.sub ?? '-';
  return `${GRID_STATE_STORAGE_PREFIX}${tenantId}:${userId}:${gridKey}`;
}

/**
 * Strip row data, transient UI state, and the framework-managed `__check__` /
 * `_spacer` columns from an exported state, leaving only layout preferences.
 */
export function normalizeLayoutForSave(state: any): any {
  const { rows, preferencePanel, ...layoutState } = (state ?? {}) as any;
  if (layoutState?.columns?.orderedFields) {
    layoutState.columns.orderedFields = orderWithGroupingColumn(
      layoutState.columns.orderedFields,
      layoutState.rowGrouping?.model,
    );
  }
  if (layoutState?.columns?.dimensions) {
    delete layoutState.columns.dimensions['__check__'];
    delete layoutState.columns.dimensions['_spacer'];
  }
  return layoutState;
}

/**
 * Prepare a stored layout for `restoreState`: re-pin `__check__` at position 0 and the row-grouping
 * column right after it (DataGrid appends columns missing from `orderedFields` at the end, so both
 * would otherwise jump to the right), and drop the `_spacer`/`__check__` dimensions. The grouping
 * fix also heals layouts stored before {@link normalizeLayoutForSave} started writing it.
 */
export function normalizeLayoutForApply(saved: any): any {
  if (!saved) return saved;
  if (saved?.columns?.orderedFields) {
    saved.columns.orderedFields = [
      '__check__',
      ...orderWithGroupingColumn(saved.columns.orderedFields, saved.rowGrouping?.model),
    ];
  }
  if (saved?.columns?.dimensions) {
    delete saved.columns.dimensions['__check__'];
    delete saved.columns.dimensions['_spacer'];
  }
  return saved;
}

/** Capture the grid's current layout (normalized), or null if unavailable. */
export function exportGridLayout(apiRef: RefObject<GridApi | null>): any | null {
  try {
    const state = apiRef.current?.exportState();
    if (!state) return null;
    return normalizeLayoutForSave(state);
  } catch {
    // exportState may throw if the grid is unmounted
    return null;
  }
}

/** Apply a (possibly stored) layout to the grid. */
export function applyGridLayout(apiRef: RefObject<GridApi | null>, layout: any): void {
  if (!layout) return;
  try {
    const prepared = normalizeLayoutForApply(structuredClone(layout));
    apiRef.current?.restoreState(prepared);
  } catch {
    // ignore malformed/incompatible layout
  }
}

/**
 * Re-apply ONLY the column layout (visibility/order/width) — used after a column
 * rebuild, which is the only thing that resets those. Deliberately leaves the
 * filter / sort / grouping / pagination models untouched so it can never clobber
 * an active filter or sort.
 */
export function applyGridColumns(apiRef: RefObject<GridApi | null>, layout: any): void {
  if (!layout?.columns) return;
  try {
    // Normalize the WHOLE layout, then restore only the columns: the grouping column's position
    // is derived from `rowGrouping.model`, which a columns-only clone would not carry.
    const prepared = normalizeLayoutForApply(structuredClone(layout));
    apiRef.current?.restoreState({ columns: prepared.columns });
  } catch {
    // ignore malformed/incompatible layout
  }
}

/** Read the transient last-used layout from localStorage (null if absent/corrupt). */
export function readSavedLayout(gridKey: string): any | null {
  try {
    const raw = localStorage.getItem(gridStateStorageKey(gridKey));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Persist the transient last-used layout to localStorage. */
export function writeSavedLayout(gridKey: string, layout: any): void {
  try {
    localStorage.setItem(gridStateStorageKey(gridKey), JSON.stringify(layout));
  } catch {
    // ignore quota / serialization errors
  }
}

/** Whether a transient layout has been stored for this grid on this device. */
export function hasSavedLayout(gridKey: string): boolean {
  try {
    return localStorage.getItem(gridStateStorageKey(gridKey)) != null;
  } catch {
    return false;
  }
}
